<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import PerformanceVisualImage from '@/core/performance-visuals/PerformanceVisualImage.vue';
import { getPerformanceLevelImageUrl } from '@/utils/performance.utils';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    imageUrl?: string | null;
    performanceLevel?: Components.Schemas.PerformanceLevelV2Dto | null;
    selected?: boolean;
    variant?: 'compact' | 'default' | 'report' | 'reportDense';
  }>(),
  {
    imageUrl: undefined,
    performanceLevel: undefined,
    selected: false,
    variant: 'default',
  },
);

const resolvedImageUrl = computed<string | null>(() => {
  return props.imageUrl ?? (props.performanceLevel ? getPerformanceLevelImageUrl(props.performanceLevel) : null);
});

const borderRadiusClass = computed<string>(() => {
  if (props.variant === 'compact') return '!rounded-checkbox';
  if (props.variant === 'report' || props.variant === 'reportDense') return '!rounded-md';
  return '!rounded-sm';
});

const imageSizeClass = computed<string>(() => {
  if (props.variant === 'compact') return 'size-icon-xs';
  if (props.variant === 'report') return 'size-9';
  if (props.variant === 'reportDense') return 'size-7';
  return 'size-full scale-80';
});
</script>

<template>
  <div
    :class="[
      'flex shrink-0 items-center justify-center bg-white',
      'border-1',
      borderRadiusClass,
      variant === 'report' && 'size-12 p-[5px]',
      variant === 'reportDense' && 'size-10 p-[5px]',
      selected ? 'border-transparent outline outline-3 outline-tint' : 'border-divider',
    ]"
  >
    <PerformanceVisualImage v-if="resolvedImageUrl" :image-url="resolvedImageUrl" :class="imageSizeClass" />
    <slot v-else>
      <span v-if="performanceLevel">{{ performanceLevel.level }}</span>
    </slot>
  </div>
</template>
